import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = { class: "modal-content" }
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["options", "value", "required", "disabled"]
const _hoisted_5 = ["value", "disabled"]
const _hoisted_6 = ["columns"]
const _hoisted_7 = ["options", "value", "required", "disabled"]
const _hoisted_8 = ["value"]
const _hoisted_9 = { key: 0 }
const _hoisted_10 = ["value"]
const _hoisted_11 = ["options", "values", "disabled"]
const _hoisted_12 = ["title"]
const _hoisted_13 = { slot: "footer" }
const _hoisted_14 = ["disabled"]

import { computed, ref, reactive } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { objValuesToString } from '../../utils/settings';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';

import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';


export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceInstanceModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');
// Model instance, used only for API calls
const serviceModel = ref<any>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const formNamespace = ref('');
const formName = ref('');
const formCatalogService = ref('');

const initialBoundApps = ref<string[]>([]);
const selectedApps = ref<string[]>([]);
const chartValues = reactive<Record<string, any>>({});
const validChartValues = ref<Record<string, boolean>>({});
const saving = ref(false);
const errors = ref<string[]>([]);

// Captured separately so background list polls (which omit internal_routes) can't wipe it
const internalRoutes = ref<string[]>([]);

const namespaces = computed(() =>
  sortBy(store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE), 'meta.name', false) as any[]
);

const namespaceOpts = computed(() =>
  namespaces.value.map((ns: any) => ({ label: ns.meta?.name || '', value: ns.meta?.name || '' }))
);

const catalogServices = computed(() =>
  store.getters['epinio/all'](EPINIO_TYPES.CATALOG_SERVICE)
);

const catalogServiceOpts = computed(() =>
  catalogServices.value.map((cs: any) => ({
    label: `${cs.name} (${cs.short_description})`,
    value: cs.name,
  }))
);

const nsAppOptions = computed(() => {
  if (!formNamespace.value) return [];

  return store.getters['epinio/all'](EPINIO_TYPES.APP)
    .filter((a: any) => a.meta.namespace === formNamespace.value)
    .map((a: any) => ({ label: a.meta.name, value: a.meta.name }));
});

const selectedCatalogService = computed(() =>
  catalogServices.value.find((cs: any) => cs.name === formCatalogService.value)
);

const showChartValues = computed(() =>
  Object.keys(selectedCatalogService.value?.settings || {}).length !== 0
);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const isDirty = computed(() => {
  if (isView.value) return false;

  if (isEdit.value) {
    if (!serviceModel.value) return false;

    const settingsChanged = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(serviceModel.value.settings || {})
    );
    const appsChanged = !isEqual(
      [...selectedApps.value].sort(),
      [...initialBoundApps.value].sort()
    );

    return settingsChanged || appsChanged;
  }

  return !!(formName.value || formCatalogService.value || selectedApps.value.length || Object.keys(chartValues).length);
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (isEdit.value) {
    if (!serviceModel.value) return false;

    const newSettings = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(serviceModel.value.settings || {})
    );
    const appBindingChanged = !isEqual(
      [...selectedApps.value].sort(),
      [...initialBoundApps.value].sort()
    );

    return newSettings || appBindingChanged;
  }

  if (!formCatalogService.value) return false;
  if (!formName.value) return false;
  if (!formNamespace.value) return false;
  if (showChartValues.value && !Object.values(validChartValues.value).every((v) => !!v)) return false;

  const nameErrors = validateKubernetesName(formName.value, '', store.getters, undefined, []);
  const nsErrors = validateKubernetesName(formNamespace.value, '', store.getters, undefined, []);

  return nameErrors.length === 0 && nsErrors.length === 0;
});

function openCreate(prefilledCatalogService?: string) {
  errors.value = [];
  modalMode.value = 'create';

  serviceModel.value = null;
  formNamespace.value = namespaces.value[0]?.meta?.name || '';
  formName.value = '';
  formCatalogService.value = prefilledCatalogService || '';

  selectedApps.value = [];
  initialBoundApps.value = [];
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};

  showModal.value = true;
}

function openView(row: any) {
  errors.value = [];
  modalMode.value = 'view';

  serviceModel.value = row;
  formNamespace.value = row.meta?.namespace || '';
  formName.value = row.name || row.meta?.name || '';
  formCatalogService.value = row.catalog_service || '';
  internalRoutes.value = [];

  selectedApps.value = [...(row.boundapps || [])];
  initialBoundApps.value = [...(row.boundapps || [])];

  const settings = objValuesToString(row.settings || {});

  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  Object.assign(chartValues, settings);
  validChartValues.value = {};

  showModal.value = true;

  // The list endpoint omits internal_routes, fetch the full record and capture routes locally
  // so background list polls (which also omit internal_routes) cannot wipe them.
  row.forceFetch().then(() => {
    if (!showModal.value) return;
    const id = `${ row.meta?.namespace }/${ row.meta?.name || row.name }`;
    const updated = store.getters['epinio/byId'](EPINIO_TYPES.SERVICE_INSTANCE, id);

    if (updated) {
      serviceModel.value = updated;
      internalRoutes.value = [...(updated.internal_routes || [])];
    }
  }).catch(() => {});
}

function openEdit(row: any) {
  errors.value = [];
  modalMode.value = 'edit';

  serviceModel.value = row;
  formNamespace.value = row.meta?.namespace || '';
  formName.value = row.name || row.meta?.name || '';
  formCatalogService.value = row.catalog_service || '';
  internalRoutes.value = [];

  selectedApps.value = [...(row.boundapps || [])];
  initialBoundApps.value = [...(row.boundapps || [])];

  const settings = objValuesToString(row.settings || {});

  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  Object.assign(chartValues, settings);
  validChartValues.value = {};

  showModal.value = true;

  // The list endpoint omits internal_routes, fetch the full record and capture routes locally
  // so background list polls (which also omit internal_routes) cannot wipe them.
  row.forceFetch().then(() => {
    if (!showModal.value) return;
    const id = `${ row.meta?.namespace }/${ row.meta?.name || row.name }`;
    const updated = store.getters['epinio/byId'](EPINIO_TYPES.SERVICE_INSTANCE, id);

    if (updated) {
      serviceModel.value = updated;
      internalRoutes.value = [...(updated.internal_routes || [])];
    }
  }).catch(() => {});
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  formName.value = '';
  formCatalogService.value = '';
  formNamespace.value = '';
  selectedApps.value = [];
  initialBoundApps.value = [];
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};
  errors.value = [];
  internalRoutes.value = [];
  serviceModel.value = null;
  showDiscardConfirm.value = false;
  showModal.value = false;
}

function resetChartValues() {
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};
}

async function onSubmit() {
  if (!validationPassed.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  try {
    if (!isEdit.value) {
      const svc = await store.dispatch('epinio/create', { type: EPINIO_TYPES.SERVICE_INSTANCE });

      // Capture values before closeModal() wipes form state
      const capturedNamespace = formNamespace.value;
      const capturedName = formName.value;
      const capturedSelectedApps = [...selectedApps.value];

      // Create the service instance, then bind apps and refresh in the background
      svc.metadata = { namespace: capturedNamespace, name: capturedName };
      svc.catalog_service = formCatalogService.value;

      const cleanSettings = { ...chartValues };

      delete cleanSettings.value;
      svc.settings = Object.keys(cleanSettings).length ? objValuesToString(cleanSettings) : undefined;

      await svc.create();

      // Re-assert metadata: followLink merges the sparse create response back
      // into the model, which can wipe metadata and break subsequent bind calls
      svc.metadata = { namespace: capturedNamespace, name: capturedName };

      closeModal();

      // Show the new item quickly, then bind apps and refresh again once done
      svc.forceFetch().catch(() => {});
      if (capturedSelectedApps.length) {
        Promise.all(capturedSelectedApps.map((app: string) => svc.bindApp(app)))
          .then(() => svc.forceFetch())
          .catch(() => {});
      }
    } else {
      const svc = serviceModel.value;
      const newSettings = !isEqual(
        objValuesToString(chartValues),
        objValuesToString(svc.settings || {})
      );

      if (newSettings) {
        const cleanSettings = { ...chartValues };

        delete cleanSettings.value;
        svc.settings = objValuesToString(cleanSettings);
        await svc.update();
      }

      const bindApps = selectedApps.value;
      const unbindApps = initialBoundApps.value.filter(a => !bindApps.includes(a));
      const newBindApps = bindApps.filter(a => !initialBoundApps.value.includes(a));

      closeModal();

      // Bind/unbind and refresh in the background
      Promise.all([
        ...newBindApps.map((a: string) => svc.bindApp(a)),
        ...unbindApps.map((a: string) => svc.unbindApp(a)),
      ]).catch(() => {});
      svc.forceFetch().catch(() => {});
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
  } finally {
    saving.value = false;
  }
}

__expose({ openCreate, openEdit, openView });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isEdit.value || isView.value) ? formName.value : 'Instances',
    subtitle: (isEdit.value || isView.value) ? (serviceModel.value?.stateDisplay || '') : 'Create New',
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-dropdown", {
            style: {"width":"100%"},
            options: namespaceOpts.value,
            value: formNamespace.value,
            label: "Namespace",
            required: !isView.value,
            disabled: isEdit.value || isView.value,
            placeholder: "Select a namespace",
            onDropdownChange: _cache[0] || (_cache[0] = (e) => { formNamespace.value = e.detail.value; selectedApps.value = []; })
          }, null, 40, _hoisted_4),
          _cache[7] || (_cache[7] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: formName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isEdit.value || isView.value,
            onTextInputChange: _cache[1] || (_cache[1] = (e) => { formName.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[9] || (_cache[9] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", {
          columns: (isView.value || isEdit.value) ? '4' : '1'
        }, [
          _createElementVNode("trailhand-dropdown", {
            style: _normalizeStyle((isView.value || isEdit.value) ? 'width: 100%; grid-column: span 3' : 'width: 100%'),
            options: catalogServiceOpts.value,
            value: formCatalogService.value,
            label: "Catalog Service",
            required: !isView.value,
            disabled: isEdit.value || isView.value,
            filterable: true,
            placeholder: "Select the type of service to create",
            onDropdownChange: _cache[2] || (_cache[2] = (e) => { formCatalogService.value = e.detail.value; resetChartValues(); })
          }, null, 44, _hoisted_7),
          _cache[8] || (_cache[8] = _createTextVNode()),
          (isView.value || isEdit.value)
            ? (_openBlock(), _createElementBlock("trailhand-text-input", {
                key: 0,
                value: serviceModel.value?.catalog_service_version || '',
                label: "Cat. Service Version",
                disabled: true
              }, null, 8, _hoisted_8))
            : _createCommentVNode("", true)
        ], 8, _hoisted_6),
        _cache[10] || (_cache[10] = _createTextVNode()),
        ((isView.value || isEdit.value) && internalRoutes.value.length)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", _hoisted_9, [
              _createElementVNode("trailhand-code-editor", {
                style: {"width":"100%"},
                value: internalRoutes.value.join('\n'),
                label: "Internal Routes",
                disabled: true
              }, null, 8, _hoisted_10)
            ]))
          : _createCommentVNode("", true),
        _cache[11] || (_cache[11] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-dropdown", {
            style: {"width":"100%"},
            options: nsAppOptions.value,
            values: selectedApps.value,
            label: "Bind to Application (Optional)",
            disabled: isView.value,
            multiselect: true,
            filterable: true,
            placeholder: "Select applications to bind",
            onDropdownChange: _cache[3] || (_cache[3] = (e) => { selectedApps.value = e.detail.values; })
          }, null, 40, _hoisted_11)
        ]),
        _cache[12] || (_cache[12] = _createTextVNode()),
        (showChartValues.value)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", {
              key: 1,
              title: _unref(t)('epinio.services.chartValues.title')
            }, [
              _createVNode(ChartValues, {
                value: chartValues,
                "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((chartValues) = $event)),
                chart: selectedCatalogService.value.settings,
                title: _unref(t)('epinio.services.chartValues.title'),
                mode: isEdit.value ? 'edit' : 'create',
                disabled: isView.value,
                onValid: _cache[5] || (_cache[5] = ($event: any) => (validChartValues.value = $event))
              }, null, 8, ["value", "chart", "title", "mode", "disabled"])
            ], 8, _hoisted_12))
          : _createCommentVNode("", true)
      ]),
      _cache[13] || (_cache[13] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[19] || (_cache[19] = _createTextVNode()),
    _createElementVNode("div", _hoisted_13, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[14] || (_cache[14] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[6] || (_cache[6] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[15] || (_cache[15] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[16] || (_cache[16] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[17] || (_cache[17] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[18] || (_cache[18] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !validationPassed.value || saving.value,
                onButtonClick: onSubmit
              }, _toDisplayString(saving.value ? (isEdit.value ? 'Saving...' : 'Creating...') : (isEdit.value ? _unref(t)('generic.save') : _unref(t)('generic.create'))), 41, _hoisted_14)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})